'use client';

import { useTranslations } from 'next-intl';
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useToast } from '@/app/components/ui/use-toast';
import { SITE } from '@/app/constants';
import { useGlobalModal } from '@/app/hooks';
import { useStore, useStoreApi } from '@/app/providers/store';
import type { LiveAvatar, UserInfo } from '@/app/types';
import { reportEvent } from '@/app/utils';
import { usePathname, useRouter } from '@/i18n/routing';
import { hasLiveCallConsent, saveLiveCallConsent } from '../_hooks/live-call-consent';
import { confirmLiveSeniorNotice, getLiveComplianceStatus, verifyLiveIdentity } from '../apis';
import { getLiveEntryClickPosition, LIVE_RETURN_ORIGIN_PARAM, type LiveReturnOrigin } from '../live-return-origin';
import type { LiveV1ComplianceStatusReply } from '../types';
import { isUserBannerAvatar, reportStreamElementClick } from '../utils';
import { getComplianceStep, getIdentityFormError, type IdentityFormError, LiveComplianceModal } from './compliance';
import { LiveCallConsentModal } from './LiveCallConsentModal';

interface UseLiveClaimFlowOptions {
  /** 当前入口所在页，写进直播间 URL，结束页据此决定「返回角色列表」回哪。 */
  origin: LiveReturnOrigin;
  /** 登录弹窗成功回调，用于调用方刷新自己的角色列表（游客态列表拿不到 UGC）。 */
  onLoggedIn?: () => void;
  /** Editing 仅需要登录和个人信息授权，不要求直播实名认证/年龄合规流程。 */
  requireCompliance?: boolean;
}

interface UseLiveClaimFlowResult {
  /** 正在进入直播的 avatar id，供卡片显示 loading 并禁用点击。 */
  claimingAvatarId: string | null;
  /** 点角色卡进直播间：登录 → 合规 → consent → 跳入口页（麦克风与 claim 由入口页负责）。 */
  startLiveCall: (avatarId: string, role: LiveAvatar, referElement: string) => void;
  /** 复用同一组前置校验，但执行自定义动作（新建 / 编辑角色）。 */
  runGuarded: (referElement: string, action: (user: UserInfo) => void | Promise<void>) => void;
  /** consent + 合规弹窗，调用方挂到自己的 JSX 里。 */
  modals: ReactNode;
}

/**
 * 直播入口前置流程。vidu-stream banner 与首页 s1 卡片共用，
 * 保证各入口的登录 / 合规 / consent / 埋点行为一致。
 *
 * 麦克风授权与 claim 本身在入口页 `/vidu-stream/live` 完成（见 LiveEntryBootstrap），
 * 这里只负责守卫与跳转，Web 与小程序才共用同一条主流程。
 */
export function useLiveClaimFlow({
  origin,
  onLoggedIn,
  requireCompliance = true,
}: UseLiveClaimFlowOptions): UseLiveClaimFlowResult {
  const pathname = usePathname();
  const router = useRouter();
  const t = useTranslations();
  const { toast } = useToast();
  const userInfo = useStore((s) => s.userInfo);
  const { open: openLoginDialog } = useGlobalModal('loginDialog');
  const storeApi = useStoreApi();

  const [liveCallConsentOpen, setLiveCallConsentOpen] = useState(false);
  const pendingConsentActionRef = useRef<(() => void | Promise<void>) | null>(null);
  const pendingConsentUserIdRef = useRef<string | null>(null);
  const [liveComplianceOpen, setLiveComplianceOpen] = useState(false);
  const [liveComplianceStatus, setLiveComplianceStatus] = useState<LiveV1ComplianceStatusReply | null>(null);
  const [isLiveComplianceSubmitting, setIsLiveComplianceSubmitting] = useState(false);
  const [liveIdentityError, setLiveIdentityError] = useState<IdentityFormError>();
  const pendingComplianceActionRef = useRef<(() => void | Promise<void>) | null>(null);
  const [claimingAvatarId, setClaimingAvatarId] = useState<string | null>(null);

  // App Router / 浏览器历史返回可能恢复之前的组件状态。离开列表页或从 BFCache
  // 恢复时清掉卡片 loading，避免用户返回后仍看到上一次点击的角色在加载。
  useEffect(() => {
    setClaimingAvatarId(null);
  }, [pathname]);

  useEffect(() => {
    const handlePageShow = () => setClaimingAvatarId(null);
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  const requireLogin = useCallback(
    (referElement: string, onLoggedInUser: (user: UserInfo) => void) => {
      if (userInfo) {
        onLoggedInUser(userInfo);
        return;
      }

      openLoginDialog({
        onClose: (loggedInUser) => {
          if (loggedInUser) {
            onLoggedIn?.();
            onLoggedInUser(loggedInUser);
          }
        },
      });
      reportEvent('login_pop_exposure', {
        refer_element: referElement,
        refer_page: pathname,
      });
    },
    [onLoggedIn, openLoginDialog, pathname, userInfo],
  );

  const runWithLiveCallConsent = useCallback(
    (action: () => void | Promise<void>, userIdOverride?: string) => {
      const userId = userIdOverride ?? storeApi.getState().userInfo?.id;
      if (!userId) {
        return;
      }

      if (hasLiveCallConsent(userId)) {
        void action();
        return;
      }

      pendingConsentUserIdRef.current = userId;
      pendingConsentActionRef.current = action;
      setLiveCallConsentOpen(true);
    },
    [storeApi],
  );

  const handleLiveCallConsentOpenChange = useCallback((open: boolean) => {
    setLiveCallConsentOpen(open);
    if (!open) {
      pendingConsentActionRef.current = null;
      pendingConsentUserIdRef.current = null;
    }
  }, []);

  const handleLiveCallConsentConfirm = useCallback(() => {
    const userId = pendingConsentUserIdRef.current ?? storeApi.getState().userInfo?.id;
    if (userId) {
      saveLiveCallConsent(userId);
    }

    pendingConsentUserIdRef.current = null;
    const action = pendingConsentActionRef.current;
    pendingConsentActionRef.current = null;
    if (action) {
      void action();
    }
  }, [storeApi]);

  const completeLiveCompliance = useCallback((status: LiveV1ComplianceStatusReply) => {
    setLiveComplianceStatus(status);
    if (getComplianceStep(status) !== 'ready') {
      setLiveComplianceOpen(true);
      return;
    }

    setLiveComplianceOpen(false);
    const action = pendingComplianceActionRef.current;
    pendingComplianceActionRef.current = null;
    if (action) void action();
  }, []);

  const runWithLiveCompliance = useCallback(
    async (action: () => void | Promise<void>) => {
      if (SITE !== 'china') {
        await action();
        return;
      }

      pendingComplianceActionRef.current = action;
      setIsLiveComplianceSubmitting(true);
      const { data, error } = await getLiveComplianceStatus();
      setIsLiveComplianceSubmitting(false);
      if (error || !data) {
        pendingComplianceActionRef.current = null;
        toast({ type: 'error', message: '合规状态获取失败，请稍后重试。' });
        return;
      }
      completeLiveCompliance(data);
    },
    [completeLiveCompliance, toast],
  );

  const handleIdentitySubmit = useCallback(
    async (body: { real_name: string; id_no: string }) => {
      setIsLiveComplianceSubmitting(true);
      setLiveIdentityError(undefined);
      const { data, error } = await verifyLiveIdentity(body);
      setIsLiveComplianceSubmitting(false);
      if (error || !data) {
        setLiveIdentityError(getIdentityFormError(error?.reason));
        return;
      }
      completeLiveCompliance(data);
    },
    [completeLiveCompliance],
  );

  const handleSeniorNoticeConfirm = useCallback(async () => {
    setIsLiveComplianceSubmitting(true);
    const { data, error } = await confirmLiveSeniorNotice();
    setIsLiveComplianceSubmitting(false);
    if (error || !data) {
      toast({ type: 'error', message: '确认失败，请稍后重试。' });
      return;
    }
    completeLiveCompliance(data);
  }, [completeLiveCompliance, toast]);

  const handleLiveComplianceOpenChange = useCallback((open: boolean) => {
    setLiveComplianceOpen(open);
    if (!open) {
      pendingComplianceActionRef.current = null;
      setLiveIdentityError(undefined);
    }
  }, []);

  /**
   * 跳直播入口页。claim 与麦克风授权都在入口页做，这里只把角色快照与来源交接过去。
   */
  const goToLiveEntry = (avatarId: string, role: LiveAvatar) => {
    const trimmedAvatarId = avatarId.trim();

    // 进入中：正在处理的卡片再次点击忽略；点其他卡片弹提示，避免多开。
    if (claimingAvatarId) {
      if (trimmedAvatarId !== claimingAvatarId) {
        toast({ type: 'info', message: t('vidu-stream.banner.claimInProgress') });
      }
      return;
    }

    if (!trimmedAvatarId) {
      toast({ type: 'error', message: t('live.claimError') });
      return;
    }

    setClaimingAvatarId(trimmedAvatarId);
    // 有意预置列表角色快照供入口页按 avatar_id 复用，以省去一次详情请求；字段新鲜度以列表数据为准。
    storeApi.getState().setLiveAvatar(role);
    // 带上 from：结束页「返回角色列表」回到进来的那个页面，同样不能依赖内存 store。
    const query = new URLSearchParams({
      avatar_id: trimmedAvatarId,
      [LIVE_RETURN_ORIGIN_PARAM]: origin,
    });
    router.push(`/vidu-stream/live?${query.toString()}`);
  };

  const runGuarded = (referElement: string, action: (user: UserInfo) => void | Promise<void>) => {
    requireLogin(referElement, (loggedInUser) => {
      if (!requireCompliance) {
        void runWithLiveCallConsent(() => action(loggedInUser), loggedInUser.id);
        return;
      }
      void runWithLiveCompliance(() => {
        runWithLiveCallConsent(() => action(loggedInUser), loggedInUser.id);
      });
    });
  };

  const startLiveCall = (avatarId: string, role: LiveAvatar, referElement: string) => {
    // element_name 三个入口共用，页面维度靠 click_position 区分：
    // 这样既能按 element_name 汇总总点击，也能按 click_position 拆分来源。
    reportStreamElementClick({
      element_name: isUserBannerAvatar(role) ? 'ugc_chat_click' : 'pgc_character_template_click',
      stream_character_name: role.name,
      click_position: getLiveEntryClickPosition(origin),
    });
    runGuarded(referElement, () => goToLiveEntry(avatarId, role));
  };

  const modals = (
    <>
      <LiveCallConsentModal
        open={liveCallConsentOpen}
        onOpenChange={handleLiveCallConsentOpenChange}
        onConfirm={handleLiveCallConsentConfirm}
      />
      {SITE === 'china' && (
        <LiveComplianceModal
          open={liveComplianceOpen}
          onOpenChange={handleLiveComplianceOpenChange}
          status={liveComplianceStatus}
          submitting={isLiveComplianceSubmitting}
          identityError={liveIdentityError}
          onIdentityErrorChange={setLiveIdentityError}
          onIdentitySubmit={handleIdentitySubmit}
          onSeniorNoticeConfirm={handleSeniorNoticeConfirm}
        />
      )}
    </>
  );

  return { claimingAvatarId, startLiveCall, runGuarded, modals };
}
